import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./RodsSelector.css";

import { ReactComponent as C7 } from "../../../assets/images/atlas/C7.svg";
import { ReactComponent as L1 } from "../../../assets/images/atlas/L1.svg";
import { ReactComponent as L2 } from "../../../assets/images/atlas/L2.svg";
import { ReactComponent as L3 } from "../../../assets/images/atlas/L3.svg";
import { ReactComponent as L4 } from "../../../assets/images/atlas/L4.svg";
import { ReactComponent as L5WhenL6 } from "../../../assets/images/atlas/L5 when L6.svg";
import { ReactComponent as L5 } from "../../../assets/images/atlas/L5.svg";
import { ReactComponent as LSTV } from "../../../assets/images/atlas/LSTV (L6).svg";
import { ReactComponent as SACRUM } from "../../../assets/images/atlas/SACRUM.svg";
import { ReactComponent as T1 } from "../../../assets/images/atlas/T1.svg";
import { ReactComponent as T10 } from "../../../assets/images/atlas/T10.svg";
import { ReactComponent as T11 } from "../../../assets/images/atlas/T11.svg";
import { ReactComponent as T12 } from "../../../assets/images/atlas/T12.svg";
import { ReactComponent as T13 } from "../../../assets/images/atlas/T13.svg";
import { ReactComponent as T2 } from "../../../assets/images/atlas/T2.svg";
import { ReactComponent as T3 } from "../../../assets/images/atlas/T3.svg";
import { ReactComponent as T4 } from "../../../assets/images/atlas/T4.svg";
import { ReactComponent as T5 } from "../../../assets/images/atlas/T5.svg";
import { ReactComponent as T6 } from "../../../assets/images/atlas/T6.svg";
import { ReactComponent as T7 } from "../../../assets/images/atlas/T7.svg";
import { ReactComponent as T8 } from "../../../assets/images/atlas/T8.svg";
import { ReactComponent as T9 } from "../../../assets/images/atlas/T9.svg";
import {
  ActNatures,
  OsteotomyTypes,
} from "../../../classes/enums/SurgicalActsEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";

const RodsSelector = ({
  rods = [],
  activeRodId = null,
  onVertebraeSelect = () => {},
  hasL6 = false,
  hasT13 = false,
  onScrewToggle = () => {},
  onUpdateRod = () => {},
  onAddRod = () => {},
  screwSpecs = {},
  setScrewSpecs = () => {},
  surgicalActs = {},
  onFirstVertebraClick = () => {},
  onSecondVertebraClick = () => {},
  availableVertebraItems = [],
  getSelectableVertebrae = () => new Set(),
  getOccupiedVertebrae = () => ({ occupied: new Set(), leftOccupied: new Set(), rightOccupied: new Set() }),
  onRemoveRod = () => {},
}) => {
  const [hoveredVertebra, setHoveredVertebra] = useState(null);
  const [selectingRodId, setSelectingRodId] = useState(null); // Track which rod we're selecting for
  const [pendingNewRodVertebra, setPendingNewRodVertebra] = useState(null); // Store vertebra to apply to new rod

  const [rodSegments, setRodSegments] = useState({ left: [], right: [] });

  const spineColumnRef = useRef(null);
  const updateTimeoutRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const vertebrae = useMemo(() => {
    const baseVertebrae = [
      { id: "C7", label: "C7", type: "cervical", Component: C7 },
      { id: "T1", label: "T1", type: "thoracic", Component: T1 },
      { id: "T2", label: "T2", type: "thoracic", Component: T2 },
      { id: "T3", label: "T3", type: "thoracic", Component: T3 },
      { id: "T4", label: "T4", type: "thoracic", Component: T4 },
      { id: "T5", label: "T5", type: "thoracic", Component: T5 },
      { id: "T6", label: "T6", type: "thoracic", Component: T6 },
      { id: "T7", label: "T7", type: "thoracic", Component: T7 },
      { id: "T8", label: "T8", type: "thoracic", Component: T8 },
      { id: "T9", label: "T9", type: "thoracic", Component: T9 },
      {
        id: "T10",
        label: "T10",
        type: "thoracic",
        Component: T10,
      },
      { id: "T11", label: "T11", type: "thoracic", Component: T11 },
      { id: "T12", label: "T12", type: "thoracic", Component: T12 },
    ];

    if (hasT13) {
      baseVertebrae.push({
        id: "T13",
        label: "T13",
        type: "thoracic",
        Component: T13,
      });
    }

    baseVertebrae.push(
      { id: "L1", label: "L1", type: "lumbar", Component: L1 },
      { id: "L2", label: "L2", type: "lumbar", Component: L2 },
      { id: "L3", label: "L3", type: "lumbar", Component: L3 },
      { id: "L4", label: "L4", type: "lumbar", Component: L4 }
    );

    if (hasL6) {
      baseVertebrae.push(
        {
          id: "L5",
          label: "L5",
          type: "lumbar",
          Component: L5WhenL6,
        },
        {
          id: "L6",
          label: "LSTV",
          type: "lumbar",
          Component: LSTV,
        }
      );
    } else {
      baseVertebrae.push({
        id: "L5",
        label: "L5",
        type: "lumbar",
        Component: L5,
      });
    }

    baseVertebrae.push({
      id: "SACRUM",
      label: "SACRUM",
      type: "sacral",
      Component: SACRUM,
    });

    return baseVertebrae;
  }, [hasL6, hasT13]);

  const vertebraOrder = useMemo(() => {
    const order = [];
    vertebrae.forEach(v => {
      if (v.id === "SACRUM") {
        order.push("S1", "S2");
      } else {
        order.push(v.id);
      }
    });
    return order;
  }, [vertebrae]);

  const standardScrewDiameters = useMemo(() => [4.5, 5.5, 6.0, 6.25, 6.5, 7.0], []);
  const standardScrewLengths = useMemo(() => [25, 30, 35, 40, 45, 50, 55], []);
  const sacrumScrewDiameters = useMemo(
    () => [5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10],
    []
  );
  const sacrumScrewLengths = useMemo(
    () => [45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100],
    []
  );

  const generateScrewOptions = useCallback((diameters, lengths) => {
    const options = [];
    diameters.forEach(diameter => {
      lengths.forEach(length => {
        options.push({
          value: `${diameter}x${length}`,
          label: `${diameter} mm × ${length} mm`,
          diameter,
          length,
        });
      });
    });
    return options;
  }, []);

  const standardScrewOptions = useMemo(
    () => generateScrewOptions(standardScrewDiameters, standardScrewLengths),
    [generateScrewOptions, standardScrewDiameters, standardScrewLengths]
  );
  const sacrumScrewOptions = useMemo(
    () => generateScrewOptions(sacrumScrewDiameters, sacrumScrewLengths),
    [generateScrewOptions, sacrumScrewDiameters, sacrumScrewLengths]
  );

  const getVertebraType = useCallback(
    logicalId => {
      if (logicalId === "S1" || logicalId === "S2") {
        return "sacral";
      }
      if (logicalId === "SACRUM") {
        return "sacral";
      }
      if (logicalId === "L6") {
        return "lumbar";
      }
      if (logicalId === "LSTV") {
        return "lumbar";
      }
      const match = vertebrae.find(v => v.id === logicalId);
      return match?.type || "thoracic";
    },
    [vertebrae]
  );

  const getScrewOptionsForVertebra = useCallback(
    logicalId => {
      const type = getVertebraType(logicalId);
      return type === "sacral" ? sacrumScrewOptions : standardScrewOptions;
    },
    [getVertebraType, sacrumScrewOptions, standardScrewOptions]
  );

  const getDefaultScrewSpecForVertebra = useCallback(
    logicalId => {
      // Cervical (C7): 4.5mm x 30mm
      if (logicalId === "C7") {
        return { diameter: 4.5, length: 30 };
      }

      // Thoracic (T1 to T5): 4.5mm x 30mm
      // Thoracic (T6 to T12/T13): 5.5mm x 40mm
      const thoracicMatch = /^T(\d+)$/i.exec(logicalId);
      if (thoracicMatch) {
        const level = Number(thoracicMatch[1]);
        if (level <= 5) {
          return { diameter: 4.5, length: 30 };
        }
        return { diameter: 5.5, length: 40 };
      }

      // Lumbar (L1 to L5): 6.5mm x 45mm
      const lumbarMatch = /^L(\d+)$/i.exec(logicalId);
      if (lumbarMatch) {
        return { diameter: 6.5, length: 45 };
      }

      // L6/LSTV: 6.5mm x 45mm
      if (logicalId === "L6") {
        return { diameter: 6.5, length: 45 };
      }

      // LSTV: 6.5mm x 45mm
      if (logicalId === "LSTV") {
        return { diameter: 6.5, length: 45 };
      }

      // Sacrum S1: 6.5mm x 40mm
      if (logicalId === "S1") {
        return { diameter: 6.5, length: 40 };
      }

      // Sacrum S2: 7.5mm x 70mm
      if (logicalId === "S2") {
        return { diameter: 7.5, length: 70 };
      }

      const options = getScrewOptionsForVertebra(logicalId);
      if (options && options.length > 0) {
        const { diameter, length } = options[0];
        return { diameter, length };
      }
      return null;
    },
    [getScrewOptionsForVertebra]
  );

  const rodSelections = useMemo(
    () =>
      rods.map(rod => ({
        id: rod.id,
        left: rod.selectedLeftScrews || [],
        right: rod.selectedRightScrews || [],
      })),
    [rods]
  );

  const selectedLeftScrews = useMemo(
    () =>
      Array.from(
        new Set(rodSelections.flatMap(selection => selection.left || []))
      ),
    [rodSelections]
  );

  const selectedRightScrews = useMemo(
    () =>
      Array.from(
        new Set(rodSelections.flatMap(selection => selection.right || []))
      ),
    [rodSelections]
  );

  const combinedSelection = useMemo(() => {
    return [...new Set([...selectedLeftScrews, ...selectedRightScrews])];
  }, [selectedLeftScrews, selectedRightScrews]);

  useEffect(() => {
    setScrewSpecs(prevSpecs => {
      let changed = false;
      const nextSpecs = { ...prevSpecs };

      Object.keys(nextSpecs).forEach(key => {
        if (!combinedSelection.includes(key)) {
          delete nextSpecs[key];
          changed = true;
        }
      });

      combinedSelection.forEach(id => {
        if (!nextSpecs[id]) {
          const defaultSpec = getDefaultScrewSpecForVertebra(id);
          if (defaultSpec) {
            nextSpecs[id] = defaultSpec;
            changed = true;
          }
        }
      });

      if (changed) {
      }
      return changed ? nextSpecs : prevSpecs;
    });
  }, [combinedSelection, getDefaultScrewSpecForVertebra, setScrewSpecs]);

  const formatSpecValue = useCallback(spec => {
    if (!spec) return "";
    return `${spec.diameter}x${spec.length}`;
  }, []);

  const parseSpecValue = useCallback(value => {
    if (!value) return null;
    const [diameter, length] = value.split("x");
    return { diameter: Number(diameter), length: Number(length) };
  }, []);

  const handleScrewSpecChange = useCallback(
    (vertebraId, value) => {
      const parsed = parseSpecValue(value);
      if (!parsed) return;
      setScrewSpecs(prev => ({
        ...prev,
        [vertebraId]: parsed,
      }));
    },
    [parseSpecValue, setScrewSpecs]
  );

  const selectedVertebraeLeft = selectedLeftScrews;
  const selectedVertebraeRight = selectedRightScrews;
  const selectedVertebrae = useMemo(() => {
    return [...new Set([...selectedLeftScrews, ...selectedRightScrews])];
  }, [selectedLeftScrews, selectedRightScrews]);

  useEffect(() => {
    onVertebraeSelect({
      left: selectedVertebraeLeft,
      right: selectedVertebraeRight,
      combined: selectedVertebrae,
    });
  }, [
    selectedLeftScrews,
    selectedRightScrews,
    onVertebraeSelect,
    selectedVertebraeLeft,
    selectedVertebraeRight,
    selectedVertebrae,
  ]);

  const logicalToDisplay = useCallback(id => {
    if (id === "S1" || id === "S2") return "SACRUM";
    if (hasL6 && id === "L6") return "LSTV";
    return id;
  }, [hasL6]);

  const getScrewCircle = useCallback(
    (logicalId, sideClass) => {
      if (!spineColumnRef.current) return null;
      const displayId = logicalToDisplay(logicalId);
      let variantClass = "";
      if (logicalId === "S1") variantClass = ".sacrum-screw-top";
      if (logicalId === "S2") variantClass = ".sacrum-screw-bottom";

      const selector = `[data-vertebra="${displayId}"] .${sideClass}${variantClass} .screw-circle`;
      return spineColumnRef.current.querySelector(selector);
    },
    [logicalToDisplay]
  );

  const calculateRodSegments = useCallback(() => {
    if (!spineColumnRef.current) return { left: [], right: [] };

    const calculateSegmentsForSide = (selectedScrews, sideClass) => {
      if (selectedScrews.length < 2) return [];

      const sortedScrews = selectedScrews
        .map(vertebraId => ({
          id: vertebraId,
          index: vertebraOrder.indexOf(vertebraId),
        }))
        .filter(item => item.index !== -1)
        .sort((a, b) => a.index - b.index);

      if (sortedScrews.length < 2) return [];

      const screwPoints = sortedScrews
        .map(item => ({
          id: item.id,
          element: getScrewCircle(item.id, sideClass),
        }))
        .filter(point => !!point.element);

      if (screwPoints.length < 2) return [];

      const segments = [];
      const containerRect = spineColumnRef.current.getBoundingClientRect();

      for (let i = 0; i < screwPoints.length - 1; i++) {
        const currentRect = screwPoints[i].element.getBoundingClientRect();
        const nextRect = screwPoints[i + 1].element.getBoundingClientRect();

        const currentCenter = {
          x: currentRect.left + currentRect.width / 2 - containerRect.left,
          y: currentRect.top + currentRect.height / 2 - containerRect.top,
        };

        const nextCenter = {
          x: nextRect.left + nextRect.width / 2 - containerRect.left,
          y: nextRect.top + nextRect.height / 2 - containerRect.top,
        };

        const deltaX = nextCenter.x - currentCenter.x;
        const deltaY = nextCenter.y - currentCenter.y;
        const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

        segments.push({
          key: `${sideClass}-${screwPoints[i].id}-${screwPoints[i + 1].id}`,
          left: currentCenter.x - 2,
          top: currentCenter.y,
          width: length,
          transform: `rotate(${angle}deg)`,
          transformOrigin: "2px 2px",
        });
      }

      return segments;
    };

    const aggregated = { left: [], right: [] };
    rodSelections.forEach(selection => {
      aggregated.left.push(
        ...calculateSegmentsForSide(selection.left, "left-screw")
      );
      aggregated.right.push(
        ...calculateSegmentsForSide(selection.right, "right-screw")
      );
    });

    return aggregated;
  }, [getScrewCircle, rodSelections, vertebraOrder]);

  const updateRodSegments = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      const newSegments = calculateRodSegments();
      setRodSegments(newSegments);
    }, 10);
  }, [calculateRodSegments]);

  useLayoutEffect(() => {
    updateRodSegments();
  }, [updateRodSegments]);

  useEffect(() => {
    if (!spineColumnRef.current) return;

    resizeObserverRef.current = new ResizeObserver(() => {
      updateRodSegments();
    });

    resizeObserverRef.current.observe(spineColumnRef.current);

    const handleWindowResize = () => updateRodSegments();
    window.addEventListener("resize", handleWindowResize);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener("resize", handleWindowResize);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [updateRodSegments]);

  // Note: Rods are NOT excluded - they are loaded into the rods state for editing
  const getExcludedVertebrae = useCallback(surgicalActs => {
    const excluded = new Set();

    Object.values(surgicalActs).forEach(act => {
      if (act.actNature === ActNatures.Osteotomy) {
        const osteotomyType = act.details?.osteotomyType || act.osteotomyType;
        if (
          osteotomyType === OsteotomyTypes.GRADE_3_PSO ||
          osteotomyType === OsteotomyTypes.GRADE_4_PSO
        ) {
          vertebraUtils
            .expandSacrumSelection([act.vertebra])
            .forEach(vertebra => excluded.add(vertebra));
        }
      }
      // Rods are intentionally not excluded - they are loaded into the editor
    });

    return excluded;
  }, []);

  const excludedVertebrae = useMemo(
    () => getExcludedVertebrae(surgicalActs),
    [surgicalActs, getExcludedVertebrae]
  );

  const isDisplayExcluded = useCallback(
    id =>
      id === "SACRUM"
        ? ["S1", "S2"].some(logicalId => excludedVertebrae.has(logicalId))
        : excludedVertebrae.has(id),
    [excludedVertebrae]
  );

  // Get all pending first vertebrae across rods
  const pendingFirstVertebrae = useMemo(() => {
    return rods
      .filter(rod => rod.pendingFirstVertebra)
      .map(rod => rod.pendingFirstVertebra);
  }, [rods]);

  // Check if vertebra is pending (first selection, waiting for second)
  const isPending = vertebraId => {
    if (pendingFirstVertebrae.includes(vertebraId)) return true;
    if (vertebraId === "SACRUM") {
      return ["S1", "S2"].some(id => pendingFirstVertebrae.includes(id));
    }
    return false;
  };

  const isInRange = vertebraId => {
    // Check committed selections
    if (selectedVertebrae.includes(vertebraId)) return true;
    if (vertebraId === "SACRUM") {
      if (["S1", "S2"].some(id => selectedVertebrae.includes(id))) return true;
    }

    // Check pending first vertebrae (shown as selected for visual feedback)
    if (pendingFirstVertebrae.includes(vertebraId)) return true;
    if (vertebraId === "SACRUM") {
      if (["S1", "S2"].some(id => pendingFirstVertebrae.includes(id))) return true;
    }

    return false;
  };

  const shouldShowScrews = vertebraId => {
    return isInRange(vertebraId);
  };

  // Get the rod we're currently selecting for - with validation
  const selectingRod = useMemo(() => {
    if (selectingRodId) {
      const found = rods.find(r => r.id === selectingRodId);
      if (found) return found;
      // selectingRodId points to non-existent rod, will be fixed by effect below
    }
    // Default to the last rod if no valid selection
    return rods.length > 0 ? rods[rods.length - 1] : null;
  }, [selectingRodId, rods]);

  // Derive selection phase from rod state - single source of truth
  const selectionPhase = useMemo(() => {
    if (!selectingRod) return "first";

    const hasPendingFirst = !!selectingRod.pendingFirstVertebra;
    const screws = [...new Set([
      ...(selectingRod.selectedLeftScrews || []),
      ...(selectingRod.selectedRightScrews || [])
    ])];

    if (hasPendingFirst) {
      return "second";
    } else if (screws.length >= 2) {
      return "complete";
    } else {
      return "first";
    }
  }, [selectingRod]);

  const fullyOccupiedVertebrae = useMemo(() => {
    const excludingRodId =
      selectionPhase === "second" ? selectingRod?.id || null : null;
    return getOccupiedVertebrae(excludingRodId).occupied;
  }, [getOccupiedVertebrae, selectionPhase, selectingRod]);

  const isFullyOccupied = useCallback(
    logicalId => fullyOccupiedVertebrae.has(logicalId),
    [fullyOccupiedVertebrae]
  );

  const isDisplayFullyOccupied = useCallback(
    displayId =>
      displayId === "SACRUM"
        ? isFullyOccupied("S1") && isFullyOccupied("S2")
        : isFullyOccupied(displayId),
    [isFullyOccupied]
  );

  // Compute selectable vertebrae based on selection phase
  const selectableVertebrae = useMemo(() => {
    if (selectionPhase === "complete") {
      // When complete, allow selecting any valid first vertebra for new rod
      return getSelectableVertebrae(null, null);
    }
    const firstVertebra = selectingRod?.pendingFirstVertebra || null;
    return getSelectableVertebrae(selectingRod, firstVertebra);
  }, [selectionPhase, selectingRod, getSelectableVertebrae]);

  // Check if a vertebra is selectable for click
  const isSelectableForClick = useCallback(
    logicalId => {
      // During selection, check if in selectable set
      return selectableVertebrae.has(logicalId);
    },
    [selectableVertebrae]
  );

  // Single effect: manage selectingRodId and handle pending new rod vertebra
  useEffect(() => {
    if (rods.length === 0) return;

    // Priority 1: If we have a pending vertebra to apply, find an empty rod and apply it
    if (pendingNewRodVertebra) {
      const emptyRod = rods.find(rod => {
        const hasCommitted = (rod.selectedLeftScrews || []).length > 0 ||
                            (rod.selectedRightScrews || []).length > 0;
        const hasPending = !!rod.pendingFirstVertebra;
        return !hasCommitted && !hasPending;
      });

      if (emptyRod) {
        setSelectingRodId(emptyRod.id);
        onFirstVertebraClick(emptyRod, pendingNewRodVertebra);
        setPendingNewRodVertebra(null);
        return;
      }
    }

    // Priority 2: Ensure selectingRodId points to a valid rod
    const currentRodExists = selectingRodId && rods.some(r => r.id === selectingRodId);
    if (!currentRodExists) {
      // Find an empty rod or default to last rod
      const emptyRod = rods.find(rod => {
        const hasCommitted = (rod.selectedLeftScrews || []).length > 0 ||
                            (rod.selectedRightScrews || []).length > 0;
        const hasPending = !!rod.pendingFirstVertebra;
        return !hasCommitted && !hasPending;
      });
      const targetRod = emptyRod || rods[rods.length - 1];
      setSelectingRodId(targetRod.id);
    }
  }, [rods, selectingRodId, pendingNewRodVertebra, onFirstVertebraClick]);

  const handleVertebraClick = vertebra => {
    if (isDisplayExcluded(vertebra.id)) {
      return;
    }

    // Determine logical vertebra IDs (SACRUM maps to S2 for click purposes)
    const logicalId = vertebra.id === "SACRUM" ? "S2" : vertebra.id;

    // Check if this vertebra is available for selection (not excluded by surgical acts)
    const isAvailable = availableVertebraItems.some(
      item => item.value === logicalId
    );
    if (!isAvailable) {
      return;
    }

    // Check if this vertebra already has both screws (left AND right) - cannot add more
    if (isFullyOccupied(logicalId)) {
      return;
    }

    // Check if this vertebra is selectable based on contiguity rules
    if (!isSelectableForClick(logicalId)) {
      return;
    }

    if (selectionPhase === "first" && selectingRod) {
      // First click - set the first vertebra
      onFirstVertebraClick(selectingRod, logicalId);
    } else if (selectionPhase === "second" && selectingRod) {
      // Second click - set the second vertebra to complete the range
      onSecondVertebraClick(selectingRod, logicalId);
    } else if (selectionPhase === "complete") {
      // Selection complete for current rod - auto-create a new rod
      // Store the intended first vertebra and wait for new rod to be created
      setPendingNewRodVertebra(logicalId);
      onAddRod();
      // The useEffect will detect the new empty rod and apply the pending vertebra
    }
  };

  const handleScrewClick = (vertebraId, side, event) => {
    event.stopPropagation();

    const activeSet =
      side === "left" ? selectedLeftScrews : selectedRightScrews;
    const isActive = activeSet.includes(vertebraId);
    if (!isActive) {
      return;
    }

    rods.forEach(rod => {
      const selectionKey =
        side === "left" ? "selectedLeftScrews" : "selectedRightScrews";
      const otherKey =
        side === "left" ? "selectedRightScrews" : "selectedLeftScrews";
      const arr = rod[selectionKey] || [];
      if (arr.includes(vertebraId)) {
        const newScrews = arr.filter(id => id !== vertebraId);

        // If only 1 screw would remain on this side, clear the entire side
        if (newScrews.length === 1) {
          const otherSide = rod[otherKey] || [];
          // If both sides would be empty, remove the rod entirely
          if (otherSide.length === 0) {
            onRemoveRod(rod.id);
          } else {
            onUpdateRod(rod.id, prev => ({
              ...prev,
              [selectionKey]: [],
            }));
          }
        } else if (newScrews.length === 0) {
          const otherSide = rod[otherKey] || [];
          // If both sides are now empty, remove the rod
          if (otherSide.length < 2) {
            onRemoveRod(rod.id);
          } else {
            onUpdateRod(rod.id, prev => ({
              ...prev,
              [selectionKey]: [],
            }));
          }
        } else {
          onUpdateRod(rod.id, prev => ({
            ...prev,
            [selectionKey]: newScrews,
          }));
        }
      }
    });

    onScrewToggle(vertebraId, side);
  };

  const RodSegment = ({ segment, side }) => (
    <div
      key={segment.key}
      className="rod"
      style={{
        position: "absolute",
        left: `${segment.left}px`,
        top: `${segment.top}px`,
        width: `${segment.width}px`,
        height: "4px",
        backgroundColor:
          side === "right" ? "rgb(0, 55, 133)" : "rgb(0, 168, 255)",
        borderRadius: "2px",
        transform: segment.transform,
        transformOrigin: segment.transformOrigin,
        zIndex: 2,
        pointerEvents: "none",
        transition: "none",
      }}
    />
  );

  const Screw = ({ logicalId, side, isSelected, extraClass = "" }) =>
    isSelected && (
      <div
        className={`screw ${side}-screw ${extraClass} active`}
        onClick={e => handleScrewClick(logicalId, side, e)}
      >
        <div className="screw-circle">
          <div className="screw-core">
            <div className="screw-dot"></div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="rods-selector">
      <div className="spine-container">
        <div className="spine-label-column">
          {vertebrae.map(vertebra => {
            const isSacrum = vertebra.id === "SACRUM";

            return (
              <div
                key={`label-${vertebra.id}`}
                className="vertebra-label-row"
                data-vertebra={vertebra.label}
              >
                {isSacrum ? (
                  <div className="sacrum-labels">
                    <span className="sacrum-label">S1</span>
                    <span className="sacrum-label">S2</span>
                  </div>
                ) : (
                  vertebra.label
                )}
              </div>
            );
          })}
        </div>
        <div className="spine-column" ref={spineColumnRef}>
          <div className="side-indicators">
            <span className="side-indicator left">L</span>
            <span className="side-indicator right">R</span>
          </div>
          {vertebrae.map(vertebra => {
            const VertebraComponent = vertebra.Component;
            const showScrews = shouldShowScrews(vertebra.id);
            const isSacrum = vertebra.id === "SACRUM";
            const screwRows = isSacrum
              ? [
                  { key: "S1", className: "sacrum-screw-top" },
                  { key: "S2", className: "sacrum-screw-bottom" },
                ]
              : [{ key: vertebra.id, className: "" }];

            // Check if vertebra is clickable (for visual feedback)
            const logicalIdForCheck = vertebra.id === "SACRUM" ? "S2" : vertebra.id;
            const isOccupiedForSelection = isDisplayFullyOccupied(vertebra.id);

            const isClickable = !isDisplayExcluded(vertebra.id) &&
              !isOccupiedForSelection &&
              isSelectableForClick(logicalIdForCheck);
            const isUnavailableByContiguity =
              !isOccupiedForSelection &&
              !isSelectableForClick(logicalIdForCheck) &&
              !isInRange(vertebra.id);

            return (
              <div
                key={vertebra.id}
                className={`vertebra ${vertebra.type} ${
                  isInRange(vertebra.id) ? "selected" : ""
                } ${isPending(vertebra.id) ? "pending" : ""} ${
                  hoveredVertebra === vertebra.id ? "hovered" : ""
                } ${selectionPhase === "complete" ? "selection-complete" : ""} selecting ${
                  isDisplayExcluded(vertebra.id) ? "disabled" : ""
                } ${isUnavailableByContiguity ? "unavailable" : ""} ${
                  isOccupiedForSelection ? "occupied" : ""
                }`}
                onClick={() => handleVertebraClick(vertebra)}
                onMouseEnter={() => setHoveredVertebra(vertebra.id)}
                onMouseLeave={() => setHoveredVertebra(null)}
                data-vertebra={vertebra.label}
                style={{
                  cursor: !isClickable ? "default" : "pointer",
                }}
              >
                <div className="vertebra-svg-container">
                  <VertebraComponent className="vertebra-svg" />

                  {screwRows.map(row => {
                    const leftActive =
                      showScrews && selectedLeftScrews.includes(row.key);
                    const rightActive =
                      showScrews && selectedRightScrews.includes(row.key);

                    return (
                      <React.Fragment key={`${vertebra.id}-${row.key}`}>
                        <Screw
                          logicalId={row.key}
                          side="left"
                          isSelected={leftActive}
                          extraClass={row.className}
                        />
                        <Screw
                          logicalId={row.key}
                          side="right"
                          isSelected={rightActive}
                          extraClass={row.className}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Rod visualization */}
          {selectedVertebrae.length >= 2 && (
            <>
              {rodSegments.left.map(segment => (
                <RodSegment key={segment.key} segment={segment} side="left" />
              ))}
              {rodSegments.right.map(segment => (
                <RodSegment key={segment.key} segment={segment} side="right" />
              ))}
            </>
          )}
        </div>
        <div className="dropdown-column">
          <div className="hrader-screws-dropdown">
            <span className="dropdown-header-label">diameter × length</span>
          </div>
          {vertebrae.map(vertebra => {
            const showScrews = shouldShowScrews(vertebra.id);
            const isSacrum = vertebra.id === "SACRUM";
            const screwRows = isSacrum
              ? [
                  { key: "S1", className: "sacrum-screw-top" },
                  { key: "S2", className: "sacrum-screw-bottom" },
                ]
              : [{ key: vertebra.id, className: "" }];

            return (
              <div
                key={`dropdown-${vertebra.id}`}
                className="dropdown-row"
                data-vertebra={vertebra.label}
              >
                {isSacrum ? (
                  <div className="screw-size-select sacrum-row">
                    {screwRows.map(row => {
                      const leftActive =
                        showScrews && selectedLeftScrews.includes(row.key);
                      const rightActive =
                        showScrews && selectedRightScrews.includes(row.key);
                      const isActive = leftActive || rightActive;

                      if (!isActive) {
                        return (
                          <div
                            key={row.key}
                            style={{ visibility: "hidden" }}
                          ></div>
                        );
                      }

                      const screwOptions = getScrewOptionsForVertebra(row.key);
                      const dropdownValue = formatSpecValue(
                        screwSpecs[row.key]
                      );

                      return (
                        <select
                          key={row.key}
                          className="screw-size-dropdown"
                          value={dropdownValue}
                          onChange={event =>
                            handleScrewSpecChange(row.key, event.target.value)
                          }
                        >
                          {screwOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      );
                    })}
                  </div>
                ) : (
                  <div className="screw-size-select">
                    {screwRows.map(row => {
                      const leftActive =
                        showScrews && selectedLeftScrews.includes(row.key);
                      const rightActive =
                        showScrews && selectedRightScrews.includes(row.key);
                      const isActive = leftActive || rightActive;

                      if (!isActive) {
                        return null;
                      }

                      const screwOptions = getScrewOptionsForVertebra(row.key);
                      const dropdownValue = formatSpecValue(
                        screwSpecs[row.key]
                      );

                      return (
                        <select
                          key={row.key}
                          className="screw-size-dropdown"
                          value={dropdownValue}
                          onChange={event =>
                            handleScrewSpecChange(row.key, event.target.value)
                          }
                        >
                          {screwOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RodsSelector;
